import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';

const GEOCODE_ENDPOINT = 'https://ws.geonorge.no/adresser/v1/sok';
const EARTH_RADIUS_KM = 6371;
const MOBILE_BREAKPOINT = '(max-width: 989px)';
// Bumped v2 → v3 when postal-code geocoding was corrected. Postal codes used to be sent
// to Kartverket as free text, which matches house numbers ("1424" resolved to
// Valsøyfjordvegen 1424, 63°N). Those wrong coordinates were cached for 30 days, so the
// old keys must be retired rather than trusted.
const RETAILER_GEOCODE_CACHE_PREFIX = 'reborn-store-locator:geocode:v3:';
const RETAILER_GEOCODE_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
// A search that matches no retailer by text (e.g. a street address) falls back to the
// retailers within this distance of where Kartverket places the search.
const NEARBY_FALLBACK_RADIUS_KM = 50;
// MapLibre's stylesheet. It used to be a <link> rendered with the section on every page; it is
// now requested together with the MapLibre script, when the locator is first needed.
const MAPLIBRE_CSS_URL = 'https://cdn.jsdelivr.net/npm/maplibre-gl@4.7.1/dist/maplibre-gl.css';
const MAPLIBRE_CSS_ID = 'reborn-maplibre-css';
// The map starts loading a little before the locator scrolls into view, so it is ready by the
// time the visitor gets there.
const MAP_PRELOAD_MARGIN = '300px 0px';
// Kartverket is only asked about this many retailers at a time.
const GEOCODE_CONCURRENCY = 6;

/**
 * The element that actually scrolls the given element, or null when the page itself does.
 *
 * On desktop the theme scrolls `.page-wrapper` (a 100dvh box with overflow-y: auto), not the
 * window. An IntersectionObserver without that `root` is clipped by the wrapper at its own
 * edge, so its `rootMargin` never reaches past the visible area and the "load a little early"
 * margin would silently do nothing.
 *
 * @param {HTMLElement} element
 * @returns {HTMLElement | null}
 */
function findScrollParent(element) {
  for (let parent = element.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
    if (/(auto|scroll|overlay)/.test(getComputedStyle(parent).overflowY)) return parent;
  }
  return null;
}

/**
 * Loads MapLibre's stylesheet once per page and resolves when it is applied (or has failed —
 * an unstyled map is better than no map). Shared by every caller through the element id.
 *
 * @returns {Promise<void>}
 */
function ensureMapStylesheet() {
  const existing = document.getElementById(MAPLIBRE_CSS_ID);
  if (existing instanceof HTMLLinkElement && existing.sheet) return Promise.resolve();

  return new Promise((resolve) => {
    const link = existing instanceof HTMLLinkElement ? existing : document.createElement('link');
    link.addEventListener('load', () => resolve(), { once: true });
    link.addEventListener('error', () => resolve(), { once: true });

    if (!link.isConnected) {
      link.id = MAPLIBRE_CSS_ID;
      link.rel = 'stylesheet';
      link.href = MAPLIBRE_CSS_URL;
      document.head.append(link);
    }
  });
}

/**
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 */
function haversineDistanceKm(lat1, lng1, lat2, lng2) {
  const toRad = (/** @type {number} */ deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;

  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** @param {number} km */
function formatDistance(km) {
  if (km < 1) return `${Math.round(km * 1000)} m unna`;
  return `${km < 10 ? km.toFixed(1) : Math.round(km)} km unna`;
}

/**
 * Retailer lat/lng are a manually-entered override (see the `Retailer` typedef below), so
 * this must guard against more than "is it a number" — a merchant can paste in a value from
 * the wrong coordinate system (e.g. UTM easting/northing in meters, which is still a finite
 * number but wildly outside valid WGS84 degree ranges) and MapLibre throws on that rather
 * than ignoring it. Anything that fails this check is treated exactly like a missing
 * coordinate: eligible for address-based geocoding, and otherwise just listed without a pin.
 *
 * @param {unknown} lat
 * @param {unknown} lng
 * @returns {boolean}
 */
function hasValidCoordinates(lat, lng) {
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

/**
 * Lower-cases and strips diacritics so "Åsveien" matches "asveien" and "Drammen" matches
 * "drammen". Norwegian letters that do not decompose are mapped explicitly.
 *
 * @param {unknown} value
 * @returns {string}
 */
function normalizeText(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();
}

/** @param {string} query */
function tokenize(query) {
  return normalizeText(query)
    .split(/[\s,]+/)
    .filter(Boolean);
}

/** @param {string} value */
function hashString(value) {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}

/**
 * Builds the Kartverket query for a retailer. A postal code is passed as `postnummer`
 * (never as free text — see RETAILER_GEOCODE_CACHE_PREFIX); a street address is
 * narrowed by it when present. Returns null when there is nothing to geocode from.
 *
 * @param {Retailer} retailer
 * @returns {string | null}
 */
function retailerGeocodeParams(retailer) {
  const postalCode = /^\d{4}$/.test(retailer.postalCode ?? '') ? retailer.postalCode : '';
  const address = [retailer.address, !postalCode ? retailer.postalCode : '', retailer.city]
    .filter(Boolean)
    .join(', ');

  if (retailer.address) {
    const params = new URLSearchParams({ sok: address });
    if (postalCode) params.set('postnummer', postalCode);
    return params.toString();
  }

  if (postalCode) return new URLSearchParams({ postnummer: postalCode }).toString();

  return null;
}

/** @param {Retailer} retailer */
function retailerGeocodeCacheKey(retailer) {
  return `${RETAILER_GEOCODE_CACHE_PREFIX}${retailer.id}:${hashString(retailerGeocodeParams(retailer) ?? '')}`;
}

/**
 * Reads a cached geocode result for a retailer, scoped to its current address text so a
 * changed address in Admin automatically invalidates the cache.
 *
 * @param {Retailer} retailer
 * @returns {{ lat: number, lng: number } | null}
 */
function readGeocodeCache(retailer) {
  try {
    const raw = localStorage.getItem(retailerGeocodeCacheKey(retailer));
    if (!raw) return null;

    const cached = JSON.parse(raw);
    if (!hasValidCoordinates(cached.lat, cached.lng)) return null;
    if (Date.now() - cached.ts > RETAILER_GEOCODE_CACHE_TTL_MS) return null;

    return { lat: cached.lat, lng: cached.lng };
  } catch {
    return null;
  }
}

/**
 * @param {Retailer} retailer
 * @param {{ lat: number, lng: number }} coords
 */
function writeGeocodeCache(retailer, coords) {
  try {
    localStorage.setItem(retailerGeocodeCacheKey(retailer), JSON.stringify({ ...coords, ts: Date.now() }));
  } catch {
    // localStorage can be unavailable (private browsing, quota exceeded) — geocoding
    // still works for this page view, it's just re-fetched on the next visit.
  }
}

/**
 * @typedef {{
 *   id: string,
 *   name: string,
 *   address: string | null,
 *   postalCode: string | null,
 *   city: string | null,
 *   country: string | null,
 *   lat: number | null,
 *   lng: number | null,
 *   phone: string | null,
 *   email: string | null,
 *   website: string | null,
 *   categories: { name: string, handle: string }[],
 * }} Retailer
 */

export class StoreLocatorComponent extends Component {
  requiredRefs = [
    'list',
    'layout',
    'map',
    'resultCount',
    'emptyState',
    'viewTabList',
    'viewTabMap',
    'retailerData',
  ];

  /** @type {Retailer[]} */
  #retailers = [];
  /** @type {import('@theme/reborn-store-locator-map').StoreLocatorMap | undefined} */
  #map;
  /** @type {Promise<void> | undefined} */
  #mapReady;
  /** @type {Set<string>} */
  #activeCategories = new Set();
  /** The submitted search text. Empty means "no search" and the full list is shown. */
  #query = '';
  /** Where Kartverket placed the submitted search, when it is a trustworthy match for it. */
  /** @type {{ lat: number, lng: number } | null} */
  #origin = null;
  /** True while the search is waiting for Kartverket — suppresses the empty state. */
  #searching = false;
  /** @type {string | null} */
  #selectedId = null;
  /** @type {AbortController | undefined} */
  #geocodeController;
  /** Retailer ids in their server-rendered order, restored when a search is cleared. */
  /** @type {string[]} */
  #initialOrder = [];
  /** @type {Map<string, string>} */
  #haystacks = new Map();
  /** True once the map and the background geocoding have been started — only ever happens once. */
  #activated = false;
  /** @type {IntersectionObserver | undefined} */
  #mapObserver;
  /** @type {AbortController | undefined} */
  #activationController;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#retailers = JSON.parse(this.refs.retailerData.textContent || '[]');
    } catch {
      this.#retailers = [];
    }

    // An invalid stored coordinate (e.g. a value saved without its decimal separator, or a
    // UTM value pasted into a lat/lng field meant for WGS84 degrees) must be cleared before
    // the map ever runs — MapLibre throws synchronously on an out-of-range LngLat.
    for (const retailer of this.#retailers) {
      if (!hasValidCoordinates(retailer.lat, retailer.lng)) {
        retailer.lat = null;
        retailer.lng = null;
      }
    }

    this.#initialOrder = (this.refs.retailerCards ?? []).map(
      (/** @type {HTMLElement} */ card) => card.dataset.retailerId ?? ''
    );

    this.#checkDataset();

    // Clearing the field (backspace, or the browser's own "x" on type=search) restores the
    // complete list without requiring another submit.
    const input = this.refs.searchInput;
    if (input) {
      const clearIfEmpty = () => {
        if (input.value.trim() === '' && (this.#query !== '' || this.#origin)) this.#clearSearch();
      };
      input.addEventListener('input', clearIfEmpty);
      input.addEventListener('search', clearIfEmpty);
    }

    // Every active retailer is listed from the first paint — no search or category needed.
    this.#render();

    if (!this.#activated) this.#scheduleActivation();

  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelActivationTriggers();
  }

  /**
   * The cards, the count and the JSON are rendered from one array in the section, so they must
   * agree. If they do not, or the section says the list is cut short, say so in the console
   * instead of letting a truncated list pass as the whole network.
   */
  #checkDataset() {
    const cards = (this.refs.retailerCards ?? []).length;
    const expected = Number(this.dataset.retailerCount);

    if (cards !== this.#retailers.length || (Number.isFinite(expected) && expected !== cards)) {
      console.warn(
        `[reborn-store-locator] Datasettet er ikke konsistent: ${cards} kort, ${this.#retailers.length} i JSON, ${this.dataset.retailerCount} fra seksjonen.`
      );
    }

    if (this.dataset.retailerDataset === 'partial') {
      console.warn('[reborn-store-locator] Det finnes flere forhandlere enn de 250 som kan vises.');
    }
  }

  /**
   * The map (MapLibre is ~800 kB) and the background geocoding are not needed to read the list,
   * so they wait until the locator approaches the viewport or someone uses it. Without
   * IntersectionObserver the previous behaviour — start when the browser is idle — is kept.
   */
  #scheduleActivation() {
    if (!('IntersectionObserver' in window)) {
      requestIdleCallback(() => this.#activate());
      return;
    }

    this.#mapObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#activate();
      },
      { root: findScrollParent(this.refs.layout), rootMargin: MAP_PRELOAD_MARGIN }
    );
    this.#mapObserver.observe(this.refs.layout);

    // Using the locator before it was scrolled to (keyboard focus, touch, click) starts it too.
    this.#activationController = new AbortController();
    const { signal } = this.#activationController;
    for (const type of ['pointerdown', 'focusin', 'keydown']) {
      this.addEventListener(type, () => this.#activate(), { signal, passive: true });
    }
  }

  #cancelActivationTriggers() {
    this.#mapObserver?.disconnect();
    this.#mapObserver = undefined;
    this.#activationController?.abort();
    this.#activationController = undefined;
  }

  #activate() {
    if (this.#activated) return;
    this.#activated = true;
    this.#cancelActivationTriggers();

    this.#ensureMap();
    this.#geocodeMissingRetailerCoordinates();
  }

  /**
   * Retailers only need a postal code or address in Admin — latitude/longitude are an
   * optional manual override. For every retailer without usable coordinates, resolve them
   * via Kartverket and cache the result in localStorage so it isn't re-fetched on every
   * page view. Runs in the background and never blocks the first render; a retailer that
   * cannot be resolved simply stays in the list without a map marker.
   */
  async #geocodeMissingRetailerCoordinates() {
    // Invalid coordinates were already sanitized to null in connectedCallback(), so this is
    // just "does it have coordinates at all".
    const pending = this.#retailers.filter((retailer) => !hasValidCoordinates(retailer.lat, retailer.lng));

    if (pending.length === 0) return;

    // A small pool instead of one request per retailer at once: with a large network that
    // would be a burst of identical requests to Kartverket on the first visit.
    let next = 0;
    const worker = async () => {
      while (next < pending.length) await this.#geocodeRetailer(pending[next++]);
    };
    await Promise.all(Array.from({ length: Math.min(GEOCODE_CONCURRENCY, pending.length) }, worker));

    this.#render();
  }

  /** @param {Retailer} retailer */
  async #geocodeRetailer(retailer) {
    const cached = readGeocodeCache(retailer);
    if (cached) {
      retailer.lat = cached.lat;
      retailer.lng = cached.lng;
      return;
    }

    const params = retailerGeocodeParams(retailer);
    if (!params) return;

    try {
      const response = await fetch(`${GEOCODE_ENDPOINT}?${params}&treffPerSide=1`);
      if (!response.ok) throw new Error(`Geocoding failed with status ${response.status}`);

      /** @type {{ adresser?: { representasjonspunkt: { lat: number, lon: number } }[] }} */
      const data = await response.json();
      const hit = data.adresser?.[0];
      if (!hit) return;

      const coords = { lat: hit.representasjonspunkt.lat, lng: hit.representasjonspunkt.lon };
      if (!hasValidCoordinates(coords.lat, coords.lng)) return;

      retailer.lat = coords.lat;
      retailer.lng = coords.lng;
      writeGeocodeCache(retailer, coords);
    } catch (error) {
      console.warn(`[reborn-store-locator] Kunne ikke geokode «${retailer.name}»`, error);
    }
  }

  /**
   * Loads MapLibre and creates the map — at most once. Every caller (the observer, first use,
   * the "Kart" tab, selecting a card or a marker) shares the same promise, so the map can
   * never be initialised twice. A failed load is not cached: the next use tries again.
   *
   * @returns {Promise<void>}
   */
  #ensureMap() {
    if (this.#mapReady) return this.#mapReady;

    // The map container keeps its CSS height while loading, so nothing moves when it arrives.
    this.refs.map.setAttribute('aria-busy', 'true');

    this.#mapReady = (async () => {
      const [{ StoreLocatorMap }] = await Promise.all([
        import('@theme/reborn-store-locator-map'),
        ensureMapStylesheet(),
      ]);

      const lat = Number(this.dataset.defaultLat);
      const lng = Number(this.dataset.defaultLng);
      const zoom = Number(this.dataset.defaultZoom);

      this.#map = new StoreLocatorMap(this.refs.map, {
        center: Number.isFinite(lat) && Number.isFinite(lng) ? [lng, lat] : undefined,
        zoom: Number.isFinite(zoom) ? zoom : undefined,
        onSelect: (id) => this.selectRetailer({ id, expand: true }),
      });

      // First fit is instant — animating from the default Norway-wide view to the
      // retailers on load would just look like the map "jumping around".
      this.#syncMap(this.#getVisibleRetailers(), { animate: false });
      this.refs.map.removeAttribute('aria-busy');
    })().catch((error) => {
      console.warn('[reborn-store-locator] Kunne ikke laste kartet', error);
      this.#mapReady = undefined;
      this.refs.map.removeAttribute('aria-busy');
    });

    return this.#mapReady;
  }

  /** @param {SubmitEvent} event */
  handleSearch(event) {
    event.preventDefault();
    const query = this.refs.searchInput.value.trim();

    if (!query) {
      this.#clearSearch();
      return;
    }

    this.#query = query;
    this.#origin = null;
    this.#searching = true;
    // Text matches are shown immediately; Kartverket then only refines (distance, nearby
    // fallback) and never decides whether the list is shown at all.
    this.#render();
    this.#geocodeSearch(query);
  }

  #clearSearch() {
    this.#geocodeController?.abort();
    this.#query = '';
    this.#origin = null;
    this.#searching = false;
    this.#render();
  }

  /** @param {string} query */
  async #geocodeSearch(query) {
    this.#geocodeController?.abort();
    const controller = new AbortController();
    this.#geocodeController = controller;

    const trimmed = query.trim();
    const params = /^\d{4}$/.test(trimmed)
      ? new URLSearchParams({ postnummer: trimmed })
      : new URLSearchParams({ sok: trimmed });

    try {
      const response = await fetch(`${GEOCODE_ENDPOINT}?${params}&treffPerSide=1`, { signal: controller.signal });
      if (!response.ok) throw new Error(`Geocoding failed with status ${response.status}`);

      /** @type {{ adresser?: { adressetekst: string, postnummer: string, poststed: string, representasjonspunkt: { lat: number, lon: number } }[] }} */
      const data = await response.json();
      const hit = data.adresser?.[0];

      this.#origin =
        hit && this.#isRelevantHit(trimmed, hit)
          ? { lat: hit.representasjonspunkt.lat, lng: hit.representasjonspunkt.lon }
          : null;
    } catch (error) {
      if (controller.signal.aborted) return;
      console.warn('[reborn-store-locator] Geocoding failed', error);
      this.#origin = null;
    }

    if (controller.signal.aborted) return;
    this.#searching = false;
    this.#render();
  }

  /**
   * Kartverket's free-text search is fuzzy: "Ski" resolves to a ski arena in Gjerstad and a
   * business name resolves to nothing useful. A hit is only trusted as "where the customer
   * means" when it really is the thing they typed.
   *
   * @param {string} query
   * @param {{ adressetekst: string, postnummer: string, poststed: string }} hit
   */
  #isRelevantHit(query, hit) {
    if (/^\d{4}$/.test(query)) return hit.postnummer === query;

    const tokens = tokenize(query);
    if (tokens.length === 0) return false;

    // A single word with no digits is a place name: it must be the hit's own place.
    if (tokens.length === 1 && !/\d/.test(tokens[0])) return normalizeText(hit.poststed) === tokens[0];

    const hitText = normalizeText(`${hit.adressetekst} ${hit.postnummer} ${hit.poststed}`);
    return tokens.every((token) => hitText.includes(token));
  }

  handleFilterChange() {
    const checked = (this.refs.filterInputs ?? [])
      .filter((/** @type {HTMLInputElement} */ input) => input.checked)
      .map((/** @type {HTMLInputElement} */ input) => input.value);

    this.#activeCategories = new Set(checked);
    this.#render();
  }

  /** @param {Retailer} retailer */
  #haystack(retailer) {
    let haystack = this.#haystacks.get(retailer.id);
    if (haystack === undefined) {
      haystack = normalizeText([retailer.name, retailer.address, retailer.postalCode, retailer.city].join(' '));
      this.#haystacks.set(retailer.id, haystack);
    }
    return haystack;
  }

  /**
   * The single source of truth for what is shown: the list, the map markers, the count and
   * the empty state are all derived from this one array.
   *
   * @returns {Retailer[]}
   */
  #getVisibleRetailers() {
    let visible = this.#retailers;

    if (this.#activeCategories.size > 0) {
      visible = visible.filter((retailer) =>
        retailer.categories.some((category) => this.#activeCategories.has(category.handle))
      );
    }

    const tokens = tokenize(this.#query);
    if (tokens.length === 0) return visible;

    const origin = this.#origin;
    const textMatches = visible.filter((retailer) => {
      const haystack = this.#haystack(retailer);
      return tokens.every((token) => haystack.includes(token));
    });

    // A street address ("Øvre Storgate 5, Drammen") never appears in a retailer's own
    // fields, so when nothing matches the typed text, fall back to the retailers near where
    // Kartverket places that address. This is deliberately limited to address searches: a
    // city or postal-code search ("Oslo", "1424") must only return retailers that match it,
    // not whatever happens to be nearby.
    const isAddressQuery = tokens.length > 1 && tokens.some((token) => /\d/.test(token));
    const matches =
      textMatches.length === 0 && origin && isAddressQuery
        ? visible.filter(
            (retailer) =>
              hasValidCoordinates(retailer.lat, retailer.lng) &&
              haversineDistanceKm(origin.lat, origin.lng, /** @type {number} */ (retailer.lat), /** @type {number} */ (retailer.lng)) <=
                NEARBY_FALLBACK_RADIUS_KM
          )
        : textMatches;

    if (!origin) return matches;

    /** @param {Retailer} retailer */
    const distanceOf = (retailer) =>
      hasValidCoordinates(retailer.lat, retailer.lng)
        ? haversineDistanceKm(origin.lat, origin.lng, /** @type {number} */ (retailer.lat), /** @type {number} */ (retailer.lng))
        : Infinity;

    return [...matches].sort((a, b) => distanceOf(a) - distanceOf(b));
  }

  /** Re-derives list, count, empty state and map from the current search and filters. */
  #render() {
    const visible = this.#getVisibleRetailers();
    const visibleIds = new Set(visible.map((retailer) => retailer.id));

    for (const card of this.refs.retailerCards ?? []) {
      card.hidden = !visibleIds.has(card.dataset.retailerId ?? '');
    }

    this.#applyOrder(visible);
    this.#applyDistances(visible);
    this.#updateResultCount(visible.length);

    if (this.#selectedId && !visibleIds.has(this.#selectedId)) this.#clearSelection();

    this.#syncMap(visible);
  }

  /**
   * Hands the map the same retailers the list shows, then reflects which of them actually
   * have a pin (a retailer without coordinates is listed, but cannot be shown on the map).
   *
   * @param {Retailer[]} visible
   * @param {{ animate?: boolean }} [options]
   */
  #syncMap(visible, options) {
    const map = this.#map;
    if (!map) return;

    map.setRetailers(visible, options);

    for (const card of this.refs.retailerCards ?? []) {
      const button = card.querySelector('.reborn-retailer-card__map-button');
      if (button instanceof HTMLElement) button.hidden = !map.hasMarker(card.dataset.retailerId ?? '');
    }
  }

  /**
   * Orders the cards by the given retailers (distance order during a located search, the
   * original order otherwise). Hidden cards keep their relative order after the visible ones.
   *
   * @param {Retailer[]} visible
   */
  #applyOrder(visible) {
    const byDistance = this.#query !== '' && this.#origin !== null;
    const orderedIds = byDistance ? visible.map((retailer) => retailer.id) : [];
    const rest = this.#initialOrder.filter((id) => !orderedIds.includes(id));
    const finalOrder = byDistance ? [...orderedIds, ...rest] : this.#initialOrder;

    /** @type {Map<string, HTMLElement>} */
    const cardsById = new Map(
      (this.refs.retailerCards ?? []).map((/** @type {HTMLElement} */ card) => [card.dataset.retailerId ?? '', card])
    );

    // Re-appending a card moves it in the DOM and drops focus from any button inside it,
    // so only touch the DOM when the order actually changed.
    const currentOrder = [...this.refs.list.children].map((child) => /** @type {HTMLElement} */ (child).dataset.retailerId ?? '');
    if (currentOrder.join('|') === finalOrder.join('|')) return;

    for (const id of finalOrder) {
      const card = cardsById.get(id);
      if (card) this.refs.list.append(card);
    }
  }

  /** @param {Retailer[]} visible */
  #applyDistances(visible) {
    const origin = this.#query !== '' ? this.#origin : null;

    /** @type {Map<string, number>} */
    const distanceById = new Map();
    if (origin) {
      for (const retailer of visible) {
        if (!hasValidCoordinates(retailer.lat, retailer.lng)) continue;
        distanceById.set(
          retailer.id,
          haversineDistanceKm(origin.lat, origin.lng, /** @type {number} */ (retailer.lat), /** @type {number} */ (retailer.lng))
        );
      }
    }

    for (const card of this.refs.retailerCards ?? []) {
      const distanceEl = card.querySelector('.reborn-retailer-card__distance');
      if (!(distanceEl instanceof HTMLElement)) continue;

      const distance = distanceById.get(card.dataset.retailerId ?? '');
      if (distance != null) {
        distanceEl.textContent = formatDistance(distance);
        distanceEl.hidden = false;
      } else {
        distanceEl.hidden = true;
      }
    }
  }

  /** @param {{ id: string | number, expand?: boolean }} data */
  selectRetailer({ id, expand = false }) {
    const targetId = String(id);
    this.#selectedId = targetId;

    for (const card of this.refs.retailerCards ?? []) {
      const isActive = card.dataset.retailerId === targetId;
      card.classList.toggle('reborn-retailer-card--active', isActive);

      if (isActive) {
        if (expand) this.#setExpanded(card, true);
        this.#revealInList(card);
      }
    }

    this.#ensureMap().then(() => this.#map?.selectMarker(targetId));

    if (window.matchMedia(MOBILE_BREAKPOINT).matches) this.showMap();
  }

  #clearSelection() {
    this.#selectedId = null;
    for (const card of this.refs.retailerCards ?? []) card.classList.remove('reborn-retailer-card--active');
    this.#map?.clearSelection();
  }

  /**
   * Scrolls the list container (not the page) so the card is in view.
   *
   * @param {HTMLElement} card
   */
  #revealInList(card) {
    const list = this.refs.list;
    const listRect = list.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();

    if (cardRect.top < listRect.top) {
      list.scrollTo({ top: list.scrollTop - (listRect.top - cardRect.top), behavior: 'smooth' });
    } else if (cardRect.bottom > listRect.bottom) {
      list.scrollTo({ top: list.scrollTop + (cardRect.bottom - listRect.bottom), behavior: 'smooth' });
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {boolean} expanded
   */
  #setExpanded(card, expanded) {
    const button = card.querySelector('.reborn-retailer-card__more-toggle');
    if (!(button instanceof HTMLElement)) return;

    const panelId = button.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;

    button.setAttribute('aria-expanded', String(expanded));

    if (panel) {
      panel.toggleAttribute('inert', !expanded);
      panel.classList.toggle('is-expanded', expanded);
    }
  }

  /** @param {MouseEvent} event */
  toggleMore(event) {
    const button = /** @type {Element} */ (event.target).closest('.reborn-retailer-card__more-toggle');
    if (!(button instanceof HTMLElement)) return;

    const card = button.closest('.reborn-retailer-card');
    if (!(card instanceof HTMLElement)) return;

    this.#setExpanded(card, button.getAttribute('aria-expanded') !== 'true');
  }

  showList() {
    this.#setActiveView('list');
  }

  showMap() {
    this.#setActiveView('map');
    this.#ensureMap().then(() => this.#map?.resize());
  }

  /** @param {'list' | 'map'} view */
  #setActiveView(view) {
    this.refs.layout.dataset.activeView = view;
    this.refs.viewTabList.setAttribute('aria-selected', String(view === 'list'));
    this.refs.viewTabMap.setAttribute('aria-selected', String(view === 'map'));

    if (view === 'map') {
      this.refs.map.focus?.({ preventScroll: true });
    } else {
      this.refs.list.focus?.({ preventScroll: true });
    }
  }

  /** @param {number} count */
  #updateResultCount(count) {
    if (count === 0 && this.#searching) {
      this.refs.resultCount.textContent = 'Søker...';
    } else {
      this.refs.resultCount.textContent =
        count === 0 ? 'Ingen treff' : count === 1 ? '1 forhandler' : `${count} forhandlere`;
    }

    // The empty state is only for a genuinely empty result — never while a search is still
    // being resolved, and never while there are cards to show.
    this.refs.emptyState.hidden = count !== 0 || this.#searching;
  }
}

customElements.define('store-locator-component', StoreLocatorComponent);
